import React, { useState, useMemo, useEffect } from 'react';
import { AlertTriangle, ArrowRight, ChevronLeft, ChevronRight, Plus, CalendarDays } from 'lucide-react';
import { AgendaType } from '@/types';
import { useAgendaEvents } from '@/components/agenda/useAgendaEvents';
import { startOfMonth, addMonths, groupByDay, eventsInMonth } from '@/lib/dateUtils';
import { AgendaEditorialHeader } from '@/components/agenda/AgendaEditorialHeader';
import { MonthView } from '@/components/agenda/MonthView';
import { WeekView } from '@/components/agenda/WeekView';
import { ListView } from '@/components/agenda/ListView';
import { TimelineView } from '@/components/agenda/TimelineView';
import { DayDrawer } from '@/components/agenda/DayDrawer';
import { ViewSwitcher, AgendaView } from '@/components/agenda/ViewSwitcher';
import { Button, IconButton } from '@/components/ui/Button';

const Agenda: React.FC = () => {
  const { events, loading, error, reload } = useAgendaEvents();
  const [view, setView] = useState<AgendaView>('month');
  const [cursor, setCursor] = useState<Date>(() => startOfMonth(new Date()));
  const [filterTypes, setFilterTypes] = useState<Record<AgendaType, boolean>>({
    edital: true, certidao: true, contrato: true, sessao: true, tarefa: true,
  });
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);

  const filtered = useMemo(() => events.filter(e => filterTypes[e.type]), [events, filterTypes]);
  const byDay = useMemo(() => groupByDay(filtered), [filtered]);
  const deadlineSummary = useMemo(() => {
    const future = events.filter((event) => typeof event.daysRemaining === 'number' && event.daysRemaining >= 0 && event.daysRemaining <= 7);
    return {
      critical: future.filter((event) => event.severity === 'critical').length,
      attention: future.filter((event) => event.severity === 'high' || event.severity === 'attention').length,
      next: future.sort((a, b) => (a.daysRemaining || 0) - (b.daysRemaining || 0))[0],
    };
  }, [events]);

  // Atalhos de teclado (Passo 15)
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'ArrowLeft')  setCursor(c => addMonths(c, -1));
      if (e.key === 'ArrowRight') setCursor(c => addMonths(c, +1));
      if (e.key.toLowerCase() === 't') setCursor(startOfMonth(new Date()));
      if (e.key.toLowerCase() === 'm') setView('month');
      if (e.key.toLowerCase() === 'w') setView('week');
      if (e.key.toLowerCase() === 'l') setView('list');
      if (e.key.toLowerCase() === 'g') setView('timeline');
      if (e.key === 'Escape') setSelectedDay(null);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  const monthLabel = cursor.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

  return (
    <div className="px-4 sm:px-6 py-5 max-w-[1480px] mx-auto overflow-x-hidden">
      {/* Topbar interna — flex-wrap para mobile (>2 linhas se precisar) */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-subtitle dark:text-ink-subtitle-dark">
          Calendário de prazos &amp; ações
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ViewSwitcher view={view} setView={setView} />
          <div className="flex items-center gap-1">
            <IconButton
              onClick={() => setCursor(c => addMonths(c, -1))}
              aria-label="Mês anterior"
              title="← anterior"
            >
              <ChevronLeft size={14} />
            </IconButton>
            <button
              type="button"
              onClick={() => setCursor(startOfMonth(new Date()))}
              className="text-[11.5px] font-medium text-ink-text dark:text-ink-text-dark hover:text-brand-700 dark:hover:text-brand-300 px-2 py-1 rounded capitalize min-w-[90px] text-center"
              title="T - hoje"
            >
              {monthLabel}
            </button>
            <IconButton
              onClick={() => setCursor(c => addMonths(c, 1))}
              aria-label="Próximo mês"
              title="→ próximo"
            >
              <ChevronRight size={14} />
            </IconButton>
          </div>
          <Button variant="secondary" icon={<Plus size={14} />}>
            <span className="hidden sm:inline">Novo compromisso</span>
            <span className="sm:hidden">Novo</span>
          </Button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-danger-soft dark:bg-danger-soft-dark border border-[rgba(196,50,28,0.25)] flex items-center justify-between gap-3">
          <div className="text-[13px] text-danger dark:text-danger-dark">
            Erro ao carregar agenda: {error}
          </div>
          <Button variant="secondary" onClick={reload}>Tentar novamente</Button>
        </div>
      )}

      {!loading && (deadlineSummary.critical > 0 || deadlineSummary.attention > 0) && (
        <section className={[
          'mb-5 rounded-lg border p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between',
          deadlineSummary.critical > 0
            ? 'border-rose-300 bg-rose-50 dark:border-rose-900 dark:bg-rose-950/25'
            : 'border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/25',
        ].join(' ')}>
          <div className="flex items-start gap-3">
            <AlertTriangle className={deadlineSummary.critical > 0 ? 'w-5 h-5 text-rose-700 dark:text-rose-300 mt-0.5' : 'w-5 h-5 text-amber-700 dark:text-amber-300 mt-0.5'} />
            <div>
              <p className="text-sm font-semibold text-ink-title dark:text-ink-title-dark">Prazos que exigem acompanhamento</p>
              <p className="mt-0.5 text-sm text-ink-subtitle dark:text-ink-subtitle-dark">
                {deadlineSummary.critical} crítico{deadlineSummary.critical === 1 ? '' : 's'} e {deadlineSummary.attention} próximo{deadlineSummary.attention === 1 ? '' : 's'} nos próximos 7 dias.
                {deadlineSummary.next ? ` Próximo: ${deadlineSummary.next.title} (${deadlineSummary.next.daysRemaining === 0 ? 'vence hoje' : `${deadlineSummary.next.daysRemaining} dia(s)`}).` : ''}
              </p>
            </div>
          </div>
          <button type="button" onClick={() => { window.location.hash = '/performance'; }} className="inline-flex shrink-0 items-center gap-2 text-sm font-medium text-brand-700 dark:text-brand-300">
            Ver indicadores <ArrowRight className="w-4 h-4" />
          </button>
        </section>
      )}

      {/*
        Layout responsivo:
        - xl (>=1280px): grid 240px + 1fr, lado a lado
        - md-lg (768-1279px): stack vertical, esquerda acima
        - sm (<768px): stack vertical full width
      */}
      <div className="grid grid-cols-1 xl:grid-cols-[240px_1fr] gap-5 items-start">
        <AgendaEditorialHeader
          cursor={cursor}
          events={events}
          filterTypes={filterTypes}
          setFilterTypes={setFilterTypes}
          byDay={byDay}
          onCursorChange={setCursor}
          onDayClick={setSelectedDay}
        />

        <div className="min-w-0">
          {loading ? (
            <AgendaSkeleton />
          ) : events.length === 0 ? (
            <EmptyState />
          ) : view === 'month'    ? (
            <MonthView cursor={cursor} byDay={byDay} onDayClick={setSelectedDay} />
          ) : view === 'week'     ? (
            <WeekView cursor={cursor} byDay={byDay} onDayClick={setSelectedDay} />
          ) : view === 'list'     ? (
            <ListView events={filtered} onDayClick={setSelectedDay} />
          ) : (
            <TimelineView cursor={cursor} events={filtered} />
          )}
        </div>
      </div>

      {/* Drawer de dia — full-screen no mobile */}
      <DayDrawer
        day={selectedDay}
        events={selectedDay ? (byDay[selectedDay.toDateString()] ?? []) : []}
        onClose={() => setSelectedDay(null)}
      />
    </div>
  );
};

// ── Skeleton da grade (Passo 16) ────────────────────────────────────────
const AgendaSkeleton: React.FC = () => (
  <div>
    <div className="grid grid-cols-7 border-b border-line dark:border-line-dark">
      {Array.from({ length: 7 }).map((_, i) => (
        <div key={i} className="px-3.5 py-2.5 text-[10.5px] font-semibold uppercase text-ink-placeholder dark:text-ink-placeholder-dark">
          ·
        </div>
      ))}
    </div>
    <div className="grid grid-cols-7 [grid-auto-rows:minmax(112px,1fr)]">
      {Array.from({ length: 42 }).map((_, i) => {
        const lastOfRow = (i + 1) % 7 === 0;
        return (
          <div
            key={i}
            className={[
              'p-2 border-b border-line dark:border-line-dark bg-surface-alt/50 dark:bg-surface-dark-alt/50 animate-pulse',
              lastOfRow ? '' : 'border-r border-line dark:border-line-dark',
            ].join(' ')}
          />
        );
      })}
    </div>
  </div>
);

// ── Estado vazio (Passo 16) ─────────────────────────────────────────────
const EmptyState: React.FC = () => (
  <div className="flex flex-col items-center justify-center py-20 text-center">
    <CalendarDays size={40} className="text-ink-placeholder dark:text-ink-placeholder-dark mb-3" />
    <p className="text-[14px] text-ink-text dark:text-ink-text-dark mb-4">
      Nenhum compromisso neste mês
    </p>
    <Button variant="secondary" icon={<Plus size={14} />}>
      Adicionar compromisso
    </Button>
  </div>
);

export default Agenda;
